import React, { useState } from "react"
import { format } from "date-fns"
import { X } from "lucide-react"
import { useGetCommunicationCreditsQuery, useSendMessagesMutation, useAddMessageTemplateMutation } from "../../lib/apiSlice/communication/communicationSlice.tsx"
import AddCreditsModal from "./messagecenter/_addcreditsmodal.jsx"
import ComposeCmmunicationReepients from "./_composecommunicationrecepients.jsx"
import { toast } from "react-toastify"
import { getUserIdFromToken } from '../../../../serverUrl';

export default function MessageCenter() {
  const [selectedChannel, setSelectedChannel] = useState("sms")
  const [messageContent, setMessageContent] = useState("")
  const [showPreview, setShowPreview] = useState(false)
  const [subject, setSubject] = useState("")
  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendMessages] = useSendMessagesMutation();
  const [addSMSTemplate] = useAddMessageTemplateMutation();
  const userId = getUserIdFromToken()
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const { data: MessageCredits, isLoading, error } = useGetCommunicationCreditsQuery()


  const [showTemplates, setShowTemplates] = useState(false)
  const insertVariable = (variable) => {
    setMessageContent((prev) => prev + ` ${variable}`)
  }

  const loadTemplate = (template) => {
    setMessageContent(template)
    setShowTemplates(false)
  }

  const [recipients, setRecipients] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const handleRecipientsChange = (recipientInfo) => {
    setRecipients(recipientInfo?.recipients || []);
    setSelectedProperties(recipientInfo?.selectedProperties || []);
  };

  const getPreviewContent = () => {
    // Ensure recipients is always an array
    const safeRecipients = Array.isArray(recipients) ? recipients : [];

    // Get the first recipient if available, or use empty defaults
    const firstRecipient = safeRecipients.length > 0 ? safeRecipients[0] : {
      name: "Tenant",
      phone_number: "",
      property_name: "",
      unit_number: "Unit",
      rent_amount: "0",
      due_date: new Date().toISOString().split('T')[0]
    };

    // Format the rent amount with commas if it's a number
    const formattedRent = typeof firstRecipient.rent_amount === 'number'
      ? firstRecipient.rent_amount.toLocaleString()
      : firstRecipient.rent_amount;

    // Format the date to a more readable format (e.g., "5th June 2025")
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();

      // Add ordinal suffix to day
      let suffix = 'th';
      if (day % 10 === 1 && day !== 11) suffix = 'st';
      else if (day % 10 === 2 && day !== 12) suffix = 'nd';
      else if (day % 10 === 3 && day !== 13) suffix = 'rd';

      return `${day}${suffix} ${month} ${year}`;
    };



    return messageContent
      .replace(/{tenant_name}/g, firstRecipient.name || "Tenant")
      .replace(/{property_name}/g, firstRecipient.property_name)
      .replace(/{unit_number}/g, firstRecipient.unit_number || "Unit")
      .replace(/{rent_amount}/g, formattedRent)
      .replace(/{due_date}/g, formatDate(firstRecipient.due_date));
  }

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      toast.warning('Please enter a message'); // Fixed typo: waning -> warning
      return;
    }

    if (recipients.length === 0) {
      toast.warning('Please select at least one recipient'); // Fixed typo: waning -> warning
      return;
    }

    setIsSending(true);

    try {
      // Prepare personalized messages for each recipient
      const messages = recipients.map(recipient => {
        // Format phone number to 254 format
        let phone = recipient.phone_number;
        if (phone.startsWith('0')) {
          phone = `254${phone.substring(1)}`;
        } else if (phone.startsWith('+')) {
          phone = phone.substring(1);
        } else if (!phone.startsWith('254')) {
          phone = `254${phone}`;
        }

        // Replace placeholders with recipient-specific data
        const personalizedMessage = messageContent
          .replace(/{tenant_name}/g, recipient.name || 'Tenant')
          .replace(/{property_name}/g, recipient.apartment_name || 'Property')
          .replace(/{unit_number}/g, recipient.unit_number || 'Unit')
          .replace(/{rent_amount}/g,
            typeof recipient.rent_amount === 'number'
              ? recipient.rent_amount.toLocaleString()
              : String(recipient.rent_amount || "0"))
          .replace(/{due_date}/g, recipient.due_date || 'N/A');

        return {
          phone,
          personalizedMessage
        };
      });

      // Extract phone numbers and messages
      const phoneNumbers = messages.map(msg => msg.phone);
      const personalizedMessages = messages.map(msg => msg.personalizedMessage);

      // Send the request (assuming you want to send all messages at once)
      const response = await sendMessages({
        message: personalizedMessages,
        phone_numbers: phoneNumbers
      }).unwrap();

      toast.success('Messages sent successfully!');
      console.log('Response:', response);
      setMessageContent('')
    } catch (error) {
      console.error('Failed to send messages:', error);
      toast.error('Failed to send messages. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const payloadTemplate = {
    title: 'Communication',
    text: messageContent,
    created_by_message_template: userId,
    updated_by_message_template: userId,
  };
  // add the template
  const handleSaveTemplate = async () => {
    if (!messageContent.trim()) {
      toast.warning('Please enter a message before saving as template');
      return;
    }

    setIsSavingTemplate(true);
    try {

      await addSMSTemplate(payloadTemplate).unwrap()
      setMessageContent('')

      toast.success('Template saved successfully!');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template. Please try again.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  return (
    <div className="flex lg:flex-row flex-col gap-6">
      {/* Left Column - Stats */}
      <div className="w-full lg:w-1/4">
        <div className="bg-white shadow-sm mb-6 p-5 rounded-lg">
          <h2 className="mb-4 font-medium text-gray-800 text-lg">Communication Credits</h2>
          <div className="gap-2 grid grid-cols-3 mb-4">
            <div className="bg-blue-50 p-3 rounded-md text-center">
              <div className="font-bold text-blue-500 text-2xl">{MessageCredits?.total_sms_bought?.toLocaleString('en-US')}</div>
              <div className="text-gray-600 text-xs">Total Credits</div>
            </div>
            <div className="bg-green-50 p-3 rounded-md text-center">
              <div className="font-bold text-green-500 text-2xl">{MessageCredits?.total_sms_tenant?.toLocaleString('en-US')}</div>
              <div className="text-gray-600 text-xs">Used Credits</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-md text-center">
              <div className="font-bold text-purple-500 text-2xl">{MessageCredits?.remaining_sms?.toLocaleString('en-US')}</div>
              <div className="text-gray-600 text-xs">Remaining Credits</div>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-md w-full text-white transition-colors"
          >
            + Add Credits
          </button>

          {isOpen && <AddCreditsModal onClose={() => setIsOpen(false)} />}

        </div>

        <div className="hidden bg-white shadow-sm p-5 rounded-lg">
          <h2 className="mb-4 font-medium text-gray-800 text-lg">Metrics Overview</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-600 text-sm">Messages Sent (Last 30 Days)</span>
                <span className="font-medium text-sm">456</span>
              </div>
              <div className="bg-gray-200 rounded-full w-full h-2">
                <div className="bg-blue-500 rounded-full h-2" style={{ width: "45%" }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-600 text-sm">Delivery Success Rate</span>
                <span className="font-medium text-sm">98.2%</span>
              </div>
              <div className="bg-gray-200 rounded-full w-full h-2">
                <div className="bg-green-500 rounded-full h-2" style={{ width: "98.2%" }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-600 text-sm">Response Rate</span>
                <span className="font-medium text-sm">42.7%</span>
              </div>
              <div className="bg-gray-200 rounded-full w-full h-2">
                <div className="bg-orange-500 rounded-full h-2" style={{ width: "42.7%" }}></div>
              </div>
            </div>

            <div>
              <div className="mb-1">
                <span className="text-gray-600 text-sm">Channel Distribution</span>
              </div>
              <div className="flex rounded-full h-4 overflow-hidden">
                <div className="bg-blue-500 h-full" style={{ width: "60%" }}></div>
                <div className="bg-green-500 h-full" style={{ width: "30%" }}></div>
                <div className="bg-purple-500 h-full" style={{ width: "10%" }}></div>
              </div>
              <div className="flex justify-between mt-1 text-xs">
                <span className="text-blue-500">SMS: 60%</span>
                <span className="text-green-500">WhatsApp: 30%</span>
                <span className="text-purple-500">Email: 10%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Flexible layout for compose and preview */}
      <div className="flex lg:flex-row flex-col gap-6 w-full lg:w-3/4">
        {/* Compose Message Section - Always visible */}
        <div className={`bg-white rounded-lg shadow-sm p-6 ${showPreview ? "lg:w-1/2" : "w-full"}`}>
          <h1 className="mb-6 font-medium text-gray-800 text-xl">Compose New Message</h1>

          {/* Communication Channel */}
          <div className="mb-6">
            <p className="block mb-2 font-medium text-gray-700 text-sm">Communication Channel</p>
            <div className="gap-3 grid grid-cols-3">
              <button
                className={`flex items-center justify-center py-2 px-4 rounded-md ${selectedChannel === "sms"
                  ? "bg-blue-500 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                onClick={() => setSelectedChannel("sms")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2 w-5 h-5"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                SMS
              </button>
              <button
                className={`flex items-center justify-center py-2 px-4 rounded-md ${selectedChannel === "whatsapp"
                  ? "bg-green-500 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                onClick={() => setSelectedChannel("whatsapp")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2 w-5 h-5"
                >
                  <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
                  <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
                  <path d="M14 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
                  <path d="M9.5 13.5c.5 1 1.5 1 2.5 1s2-.5 2.5-1" />
                </svg>
                WhatsApp
              </button>
              <button
                className={`flex items-center justify-center py-2 px-4 rounded-md ${selectedChannel === "email"
                  ? "bg-purple-500 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                onClick={() => setSelectedChannel("email")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2 w-5 h-5"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                Email
              </button>
            </div>
          </div>

          {/* Recipients */}
          <ComposeCmmunicationReepients onRecipientsChange={handleRecipientsChange} />

          {/* Subject (for Email) */}
          {selectedChannel === "email" && (
            <div className="mb-6">
              <p className="block mb-2 font-medium text-gray-700 text-sm">Subject (for Email)</p>
              <input
                type="text"
                placeholder="Enter message subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="px-3 py-2 border border-gray-300 focus:border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              />
            </div>
          )}

          {/* Message Content */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <p className="block font-medium text-gray-700 text-sm">Message Content</p>

            </div>
            <div className="relative">
              <textarea
                className="px-3 py-2 border border-gray-300 focus:border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-h-32 resize-y"
                placeholder="Type your message here..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
              ></textarea>
              <div className="right-2 bottom-2 absolute text-gray-400 text-xs">
                {messageContent.length}/160 characters
              </div>
            </div>
          </div>

          {/* Insert Variables */}
          <div className="mb-6">
            <p className="block mb-2 font-medium text-gray-700 text-sm">Insert Variables</p>
            <div className="flex flex-wrap gap-2">
              <button
                className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-md text-gray-700 text-sm"
                onClick={() => insertVariable("{tenant_name}")}
              >
                {"{tenant_name}"}
              </button>
              <button
                className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-md text-gray-700 text-sm"
                onClick={() => insertVariable("{property_name}")}
              >
                {"{property_name}"}
              </button>
              <button
                className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-md text-gray-700 text-sm"
                onClick={() => insertVariable("{unit_number}")}
              >
                {"{unit_number}"}
              </button>
              <button
                className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-md text-gray-700 text-sm"
                onClick={() => insertVariable("{rent_amount}")}
              >
                {"{rent_amount}"}
              </button>
              <button
                className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-md text-gray-700 text-sm"
                onClick={() => insertVariable("{due_date}")}
              >
                {"{due_date}"}
              </button>

            </div>
          </div>



          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSendMessage}
              disabled={recipients.length === 0 || isSending}
              className={`flex flex-1 justify-center items-center px-4 py-2 rounded-md text-white transition-colors ${recipients.length === 0 || isSending
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600'
                }`}
            >
              {isSending ? (
                <>
                  <svg
                    className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                  Send Message
                </>
              )}
            </button>



            <button
              onClick={handleSaveTemplate}
              disabled={isSavingTemplate}
              className={`flex flex-1 justify-center items-center px-4 py-2 border border-gray-300 rounded-md transition-colors ${isSavingTemplate
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : 'bg-white hover:bg-gray-50 text-gray-700'
                }`}
            >
              {isSavingTemplate ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 w-5 h-5 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Save as Template
                </>
              )}
            </button>
            <button
              className="flex justify-center items-center bg-transparent px-4 py-2 rounded-md text-gray-600 hover:text-gray-800 transition-colors"
              onClick={() => setShowPreview(!showPreview)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {showPreview ? "Hide Preview" : "Preview"}
            </button>
          </div>
        </div>

        {/* Preview Section - Only visible when showPreview is true */}
        {showPreview && (
          <div className="bg-white shadow-sm mb-6 p-6 rounded-lg lg:w-1/2">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-lg">Message Preview</h3>
              <button variant="secondary" size="sm" onClick={() => setShowPreview(false)}>
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Mobile Phone Emulator */}
            <div className="flex justify-center">
              <div className="relative bg-gray-800 shadow-xl p-3 border-[8px] border-gray-800" style={{ height: "90vh", width: "20vw", borderRadius: "20px" }}>
                {/* Phone Notch */}
                <div className="top-0 left-1/2 absolute flex justify-center items-center bg-gray-900 rounded-b-[14px] w-[120px] h-[30px] -translate-x-1/2 transform">
                  <div className="bg-gray-800 rounded-full w-16 h-4"></div>
                </div>

                {/* Phone Screen */}
                <div className="rounded-[24px] w-full h-full overflow-hidden" style={{
                  background: selectedChannel === "whatsapp" ? "#e5ddd5" : "#f3f4f6"
                }}>
                  {/* Status Bar */}
                  <div className="flex justify-between items-center bg-gray-800 px-4 h-6 text-white text-xs">
                    <div>{new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="flex items-center space-x-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-3 h-3"
                      >
                        <path
                          fillRule="evenodd"
                          d="M1.371 8.143c5.858-5.857 15.356-5.857 21.213 0a.75.75 0 0 1 0 1.061l-.53.53a.75.75 0 0 1-1.06 0c-4.98-4.979-13.053-4.979-18.032 0a.75.75 0 0 1-1.06 0l-.53-.53a.75.75 0 0 1 0-1.06zm3.182 3.182c4.1-4.1 10.749-4.1 14.85 0a.75.75 0 0 1 0 1.061l-.53.53a.75.75 0 0 1-1.062 0 8.25 8.25 0 0 0-11.667 0 .75.75 0 0 1-1.06 0l-.53-.53a.75.75 0 0 1 0-1.06zm3.204 3.182a6 6 0 0 1 8.486 0 .75.75 0 0 1 0 1.061l-.53.53a.75.75 0 0 1-1.061 0 3.75 3.75 0 0 0-5.304 0 .75.75 0 0 1-1.06 0l-.53-.53a.75.75 0 0 1 0-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-3 h-3"
                      >
                        <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v9.375c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 0 1-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v4.875c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 0 1 3 18v-4.875z" />
                      </svg>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-3 h-3"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3.75 6.75a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3v-.037c.856-.174 1.5-.93 1.5-1.838v-2.25c0-.907-.644-1.664-1.5-1.837V9.75a3 3 0 0 0-3-3h-15zm15 1.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5h-15a1.5 1.5 0 0 1-1.5-1.5v-6a1.5 1.5 0 0 1 1.5-1.5h15z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Message Content based on channel */}
                  {selectedChannel === "sms" && (
                    <div className="flex flex-col bg-gray-100 h-[calc(100%-24px)]">
                      {/* SMS App Header */}
                      <div className="flex items-center bg-blue-500 p-3 text-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="mr-2 w-5 h-5"
                        >
                          <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 0 0-1.032-.211 50.89 50.89 0 0 0-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 0 0 2.433 3.984L7.28 21.53A.75.75 0 0 1 6 21v-4.03a48.527 48.527 0 0 1-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                          <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 0 0 1.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0 0 15.75 7.5z" />
                        </svg>
                        <span className="font-medium">Messages</span>
                      </div>

                      {/* SMS Conversation */}
                      <div className="flex-1 bg-gray-100 p-3 overflow-y-auto">
                        <div className="flex items-center mb-3">
                          <div className="flex justify-center items-center bg-blue-200 mr-2 rounded-full w-8 h-8 font-bold text-blue-600">
                            P
                          </div>
                          <div>
                            <div className="font-medium text-sm">Property Manager</div>
                            <div className="text-gray-500 text-xs">Now</div>
                          </div>
                        </div>

                        <div className="bg-white shadow-sm p-3 border border-gray-200 rounded-lg text-sm">
                          {getPreviewContent()}
                        </div>
                      </div>

                      {/* SMS Input */}
                      <div className="bottom-5 absolute flex p-2 border-gray-200 border-t w-64">
                        <div className="flex-1 bg-white px-4 py-2 rounded-full text-gray-400 text-sm">
                          Text message
                        </div>
                        <button className="flex justify-center items-center bg-blue-500 ml-2 rounded-full w-8 h-8 text-white">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-4 h-4"
                          >
                            <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedChannel === "whatsapp" && (
                    <div className="flex flex-col bg-[#e5ddd5] h-[calc(100%-24px)]">
                      {/* WhatsApp Header */}
                      <div className="flex items-center bg-green-700 p-3 text-white">
                        <div className="flex justify-center items-center bg-gray-300 mr-2 rounded-full w-8 h-8 overflow-hidden">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-6 h-6 text-gray-600"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438zM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-sm">John Kamau</div>
                          <div className="opacity-80 text-xs">online</div>
                        </div>
                        <div className="flex space-x-3 ml-auto">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M1.5 4.5a3 3 0 0 1 3-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 0 1-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 0 0 6.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 0 1 1.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 0 1-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5"
                          >
                            <path d="M4.5 4.5a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h8.25a3 3 0 0 0 3-3v-9a3 3 0 0 0-3-3H4.5zm0 1.5h8.25a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H4.5a1.5 1.5 0 0 1-1.5-1.5v-9a1.5 1.5 0 0 1 1.5-1.5zm10.5 6a1.5 1.5 0 0 0-1.5 1.5v3a1.5 1.5 0 0 0 1.5 1.5h4.5a1.5 1.5 0 0 0 1.5-1.5v-3a1.5 1.5 0 0 0-1.5-1.5h-4.5z" />
                          </svg>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10.5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>


                      {/* WhatsApp Chat */}
                      <div className="flex-1 p-3 overflow-y-auto" style={{ backgroundColor: "#e5ddd5" }}>
                        <div className="flex justify-end mb-4">
                          <div className="relative shadow-sm p-2 px-3 rounded-lg max-w-[80%]" style={{ backgroundColor: "#dcf8c6" }}>
                            <div className="text-sm">Hi, how are you?</div>
                            <div className="mt-1 text-gray-500 text-xs text-right">10:30 AM</div>
                            <div className="right-1 bottom-1 absolute text-primary">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-3 h-3"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div className="flex mb-4">
                          <div className="bg-white shadow-sm p-2 px-3 rounded-lg max-w-[80%]">
                            <div className="text-sm">I'm doing well, thanks for asking!</div>
                            <div className="mt-1 text-gray-500 text-xs text-right">10:32 AM</div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <div className="relative shadow-sm p-2 px-3 rounded-lg max-w-[80%]" style={{ backgroundColor: "#dcf8c6" }}>
                            <div className="text-sm">{getPreviewContent()}</div>
                            <div className="mt-1 text-gray-500 text-xs text-right">Just now</div>
                            <div className="right-1 bottom-1 absolute text-primary">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-3 h-3"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* WhatsApp Input */}
                      <div className="bottom-5 absolute flex p-2 w-64">
                        <div className="flex flex-1 items-center bg-white px-3 py-1 rounded-full">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="mr-2 w-5 h-5 text-gray-500"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-2.625 6c-.54 0-.828.419-.936.634a1.96 1.96 0 0 0-.189.866c0 .298.059.605.189.866.108.215.395.634.936.634.54 0 .828-.419.936-.634.13-.26.189-.568.189-.866 0-.298-.059-.605-.189-.866-.108-.215-.395-.634-.936-.634zm4.314.634c.108-.215.395-.634.936-.634.54 0 .828.419.936.634.13.26.189.568.189.866 0 .298-.059.605-.189.866-.108.215-.395.634-.936.634-.54 0-.828-.419-.936-.634a1.96 1.96 0 0 1-.189-.866c0-.298.059-.605.189-.866zm2.023 6.828a.75.75 0 1 0-1.06-1.06 3.75 3.75 0 0 1-5.304 0 .75.75 0 0 0-1.06 1.06 5.25 5.25 0 0 0 7.424 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-gray-400 text-sm">Type a message</span>
                        </div>
                        <button className="flex justify-center items-center bg-green-700 ml-2 rounded-full w-10 h-10 text-white">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5"
                          >
                            <path d="M8.25 4.5a3.75 3.75 0 1 1 7.5 0v8.25a3.75 3.75 0 1 1-7.5 0V4.5z" />
                            <path d="M6 10.5a.75.75 0 0 1 .75.75v1.5a5.25 5.25 0 1 0 10.5 0v-1.5a.75.75 0 0 1 1.5 0v1.5a6.751 6.751 0 0 1-6 6.709v2.291h3a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h3v-2.291a6.751 6.751 0 0 1-6-6.709v-1.5A.75.75 0 0 1 6 10.5z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedChannel === "email" && (
                    <div className="flex flex-col bg-gray-100 h-[calc(100%-24px)]">
                      {/* Email App Header */}
                      <div className="flex items-center bg-red-500 p-3 text-white">
                        <div className="flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="mr-2 w-5 h-5"
                          >
                            <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67z" />
                            <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908z" />
                          </svg>
                          <span className="font-medium">Gmail</span>
                        </div>
                        <div className="ml-auto">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5zM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Email Content */}
                      <div className="flex-1 bg-white overflow-y-auto">
                        <div className="p-4 border-b">
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center">
                              <div className="flex justify-center items-center bg-blue-500 mr-2 rounded-full w-8 h-8 font-bold text-white">
                                P
                              </div>
                              <div>
                                <div className="font-medium text-sm">Property Management</div>
                                <div className="text-gray-500 text-xs">to me</div>
                              </div>
                            </div>
                            <div className="text-gray-500 text-xs">10:45 AM</div>
                          </div>

                          <h2 className="mb-3 font-medium text-lg">{subject || "Important Notice"}</h2>

                          <div className="text-sm">
                            <p className="mb-3">Dear John Kamau,</p>
                            <p className="mb-3">{getPreviewContent()}</p>
                            <p className="mb-3">If you have any questions, please don't hesitate to contact us.</p>
                            <p className="mb-3">Thank you,</p>
                            <p className="font-medium">Greenview Property Management</p>
                            <p className="text-gray-500 text-xs">+254 712 345 678</p>
                          </div>
                        </div>

                        <div className="flex space-x-4 p-4">
                          <button className="flex items-center text-gray-600 text-sm">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="mr-1 w-5 h-5"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.28 7.72a.75.75 0 0 1 0 1.06l-2.47 2.47H21a.75.75 0 0 1 0 1.5H4.81l2.47 2.47a.75.75 0 1 1-1.06 1.06l-3.75-3.75a.75.75 0 0 1 0-1.06l3.75-3.75a.75.75 0 0 1 1.06 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Reply
                          </button>
                          <button className="flex items-center text-gray-600 text-sm">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="mr-1 w-5 h-5"
                            >
                              <path
                                fillRule="evenodd"
                                d="M14.47 2.47a.75.75 0 0 1 1.06 0l6 6a.75.75 0 0 1 0 1.06l-6 6a.75.75 0 1 1-1.06-1.06l4.72-4.72H9a5.25 5.25 0 1 0 0 10.5h3a.75.75 0 0 1 0 1.5H9a6.75 6.75 0 0 1 0-13.5h10.19l-4.72-4.72a.75.75 0 0 1 0-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Forward
                          </button>
                        </div>
                      </div>

                      {/* Email Bottom Bar */}
                      <div className="flex justify-between bg-gray-100 p-3 border-t">
                        <div className="flex space-x-4">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-6 h-6 text-gray-500"
                          >
                            <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67z" />
                            <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908z" />
                          </svg>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-6 h-6 text-gray-500"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.5 4.478v.227a48.816 48.816 0 0 1 3.878.512.75.75 0 1 1-.256 1.478l-.209-.035-1.005 13.07a3 3 0 0 1-2.991 2.77H8.084a3 3 0 0 1-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 0 1-.256-1.478A48.567 48.567 0 0 1 7.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 0 1 3.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 0 1 3.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 0 0-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 1 0-1.5.058l.347 9a.75.75 0 1 0 1.499-.058l-.346-9zm5.48.058a.75.75 0 1 0-1.498-.058l-.347 9a.75.75 0 0 0 1.5.058l.345-9z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-6 h-6 text-gray-500"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875zM9.75 14.25a.75.75 0 0 0 0 1.5H15a.75.75 0 0 0 0-1.5H9.75z"
                              clipRule="evenodd"
                            />
                            <path d="M14.25 5.25a5.23 5.23 0 0 0-1.279-3.434 9.768 9.768 0 0 1 6.963 6.963A5.23 5.23 0 0 0 16.5 7.5h-1.875a.375.375 0 0 1-.375-.375V5.25z" />
                          </svg>
                        </div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-6 h-6 text-gray-500"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>

                {/* Phone Home Button */}
                <div className="bottom-1 left-1/2 absolute bg-gray-800 rounded-full w-1/3 h-1 -translate-x-1/2 transform"></div>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  )
}
